/**
 * RevenueCat + StoreKit subscriptions (iOS native only).
 *
 * @author Varun Pandey
 */

import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import type {
  CustomerInfo,
  PurchasesOfferings,
  PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { isNativeIOSApp } from '@/utils/platform';
import {
  getRcPackageIdForBackendPlan,
  isIapProductMappingConfigured,
  RC_DEFAULT_OFFERING_ID,
} from '@/data/iapProductMapping';

let configured = false;
let configurePromise: Promise<void> | null = null;
let cachedOfferings: PurchasesOfferings | null = null;

export type IosPackagePriceMap = Record<string, string>;

function getIosApiKey(): string {
  return import.meta.env.VITE_REVENUECAT_IOS_API_KEY?.trim() ?? '';
}

export function isRevenueCatSdkAvailable(): boolean {
  return isNativeIOSApp() && Boolean(getIosApiKey());
}

export function isRevenueCatEnabled(): boolean {
  return isRevenueCatSdkAvailable() && isIapProductMappingConfigured();
}

function isPurchaseCancelledError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; message?: string; userCancelled?: boolean };
  if (err.userCancelled === true) return true;
  if (err.code === '1' || err.code === 'PURCHASE_CANCELLED_ERROR') return true;
  const msg = String(err.message ?? '').toLowerCase();
  return msg.includes('cancel') || msg.includes('cancelled');
}

async function ensureConfigured(appUserId: string): Promise<void> {
  if (!isNativeIOSApp()) return;
  const apiKey = getIosApiKey();
  if (!apiKey) {
    throw new Error('RevenueCat iOS API key is not configured (VITE_REVENUECAT_IOS_API_KEY).');
  }

  if (configured) {
    await Purchases.logIn({ appUserID: appUserId });
    return;
  }

  if (!configurePromise) {
    configurePromise = (async () => {
      try {
        if (import.meta.env.DEV) {
          await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
        }
        await Purchases.configure({
          apiKey,
          appUserID: appUserId,
        });
        configured = true;
      } catch (error) {
        configurePromise = null;
        configured = false;
        throw error;
      }
    })();
  }

  await configurePromise;
}

/** Await RevenueCat configure + login before presenting StoreKit. */
export async function ensureRevenueCatReady(appUserId: string): Promise<void> {
  if (!isRevenueCatSdkAvailable()) {
    throw new Error('In-app purchases are not configured for this build.');
  }
  await ensureConfigured(appUserId);
}

export async function initRevenueCatForSession(appUserId: string | null | undefined): Promise<void> {
  if (!isRevenueCatEnabled() || !appUserId) return;
  try {
    await ensureConfigured(appUserId);
  } catch (error) {
    console.warn('[RevenueCat] init failed:', error);
  }
}

export async function logOutRevenueCat(): Promise<void> {
  if (!isNativeIOSApp() || !configured) return;
  try {
    await Purchases.logOut();
  } catch (error) {
    console.warn('[RevenueCat] logOut failed:', error);
  }
}

function iterOfferingsPackages(
  offerings: PurchasesOfferings,
): PurchasesPackage[] {
  const allOfferings = offerings.all ?? {};
  const preferred = offerings.current ?? allOfferings[RC_DEFAULT_OFFERING_ID];
  const pools = [
    preferred,
    ...Object.values(allOfferings),
  ].filter(Boolean) as Array<{ availablePackages?: PurchasesPackage[] }>;

  const seen = new Set<string>();
  const packages: PurchasesPackage[] = [];
  for (const offering of pools) {
    for (const pkg of offering.availablePackages ?? []) {
      if (!seen.has(pkg.identifier)) {
        seen.add(pkg.identifier);
        packages.push(pkg);
      }
    }
  }
  return packages;
}

export function buildIosPackagePriceMapFromOfferings(
  offerings: PurchasesOfferings,
): IosPackagePriceMap {
  const map: IosPackagePriceMap = {};
  for (const pkg of iterOfferingsPackages(offerings)) {
    const priceString = pkg.product?.priceString;
    if (priceString) {
      map[pkg.identifier] = priceString;
    }
  }
  return map;
}

export async function fetchOfferings(appUserId: string): Promise<PurchasesOfferings> {
  await ensureConfigured(appUserId);
  const offerings = await Purchases.getOfferings();
  cachedOfferings = offerings;
  return offerings;
}

export function getCachedRcPackage(rcPackageId: string): PurchasesPackage | null {
  if (!cachedOfferings) return null;
  return findPackage(cachedOfferings, rcPackageId);
}

export async function fetchIosPackagePriceMap(appUserId: string): Promise<IosPackagePriceMap> {
  const offerings = await fetchOfferings(appUserId);
  return buildIosPackagePriceMapFromOfferings(offerings);
}

function findPackage(
  offerings: PurchasesOfferings,
  rcPackageId: string,
): PurchasesPackage | null {
  for (const pkg of iterOfferingsPackages(offerings)) {
    if (pkg.identifier === rcPackageId) return pkg;
  }
  return null;
}

export interface IapPurchaseResult {
  customerInfo: CustomerInfo;
  productIdentifier: string;
  rcPackageIdentifier: string;
}

export interface PurchaseSubscriptionOptions {
  /** Skip getOfferings when the package was already resolved (e.g. from price hook). */
  preloadedPackage?: PurchasesPackage | null;
}

export async function purchaseRcPackage(
  appUserId: string,
  rcPackageId: string,
  options?: PurchaseSubscriptionOptions,
): Promise<IapPurchaseResult> {
  if (!isRevenueCatSdkAvailable()) {
    throw new Error('In-app purchases are not configured for this build.');
  }

  await ensureRevenueCatReady(appUserId);

  let aPackage =
    options?.preloadedPackage?.identifier === rcPackageId
      ? options.preloadedPackage
      : getCachedRcPackage(rcPackageId);

  if (!aPackage) {
    const offerings = await fetchOfferings(appUserId);
    aPackage = findPackage(offerings, rcPackageId);
  }

  if (!aPackage) {
    throw new Error(
      `Package "${rcPackageId}" was not found in RevenueCat offerings.`,
    );
  }

  try {
    const result = await Purchases.purchasePackage({ aPackage });
    return {
      customerInfo: result.customerInfo,
      productIdentifier: result.productIdentifier,
      rcPackageIdentifier: rcPackageId,
    };
  } catch (error) {
    if (isPurchaseCancelledError(error)) {
      const cancelled = new Error('Payment cancelled by user') as Error & {
        code: string;
      };
      cancelled.code = 'PAYMENT_CANCELLED';
      throw cancelled;
    }
    throw error;
  }
}

export async function purchaseSubscriptionForBackendPlan(
  backendPlanId: string,
  appUserId: string,
  categoryName?: string | null,
  options?: PurchaseSubscriptionOptions,
): Promise<IapPurchaseResult> {
  if (!isRevenueCatEnabled()) {
    throw new Error('In-app purchases are not configured for this build.');
  }

  const rcPackageId = getRcPackageIdForBackendPlan(backendPlanId, categoryName);
  if (!rcPackageId) {
    throw new Error(
      'No App Store package mapped for this plan. Check VITE_RC_PKG_{TIER}_{QUARTERLY|YEARLY} env variables.',
    );
  }

  return purchaseRcPackage(appUserId, rcPackageId, options);
}

export async function restorePurchases(appUserId: string): Promise<CustomerInfo> {
  if (!isRevenueCatEnabled()) {
    throw new Error('In-app purchases are not configured for this build.');
  }
  await ensureConfigured(appUserId);
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

export async function getCustomerInfo(appUserId: string): Promise<CustomerInfo> {
  await ensureConfigured(appUserId);
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

/** Active entitlement identifiers from RevenueCat (configure in RC dashboard). */
export function getActiveEntitlementIds(customerInfo: CustomerInfo): string[] {
  const active = customerInfo.entitlements?.active ?? {};
  return Object.keys(active);
}
